// Onboarding draft — lives in memory only (PHASE1_FLOWS §4).
// Refreshing or closing loses it. No localStorage for draft data.
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type {
  AssistedByDetails,
  IdentityCaptures,
  KnowYouDetails,
  Nominee,
  OnboardingDraft,
  PersonalDetails,
  ProductType,
  SubmitResult,
} from '@/types/onboarding.types';

export const emptyPersonal: PersonalDetails = {
  salutation: '',
  gender: '',
  firstAndMiddleNames: '',
  surname: '',
  dobDay: '',
  dobMonth: '',
  dobYear: '',
  aadhaar: '',
  pan: '',
  email: '',
  mobile: '',
  residentOfIndia: '',
};

export const emptyNominee = (): Nominee => ({
  fullName: '',
  relationship: '',
  dob: '',
  sharePercent: '',
  addressSameAsMine: true,
  address: '',
  guardianName: '',
  guardianRelationship: '',
});

export const emptyKnowYou = (): KnowYouDetails => ({
  fatherName: '',
  motherName: '',
  maritalStatus: '',
  spouseName: '',
  currentAddressSame: '',
  addressLine1: '',
  addressLine2: '',
  landmark: '',
  pinCode: '',
  city: '',
  state: '',
  residenceType: '',
  yearsAtAddress: '',
  employmentStatus: '',
  employerName: '',
  industry: '',
  designation: '',
  annualIncome: '',
  sourcesOfFunds: [],
  otherSource: '',
  purpose: '',
  monthlyDeposits: '',
  qualification: '',
  wantsNominee: '',
  noNomineeConfirm: false,
  nominationType: '',
  nominees: [emptyNominee()],
  isPep: '',
  pepRelationship: '',
  pepPosition: '',
  taxResidentOther: '',
  taxRows: [{ country: '', tin: '', noTinReason: '' }],
  countryOfBirth: 'India',
  cityOfBirth: '',
  citizenship: 'India',
  usPerson: '',
  residentialStatus: 'RESIDENT',
  accessibility: '',
});

export const emptyAssistedBy = (): AssistedByDetails => ({
  wasAssisted: '',
  channel: '',
  agentCode: '',
  representativeName: '',
  branchCode: '',
  branch: '',
  referral: '',
  corporateCode: '',
  promoCode: '',
});

const initialDraft = (): OnboardingDraft => ({
  product: null,
  consentsAccepted: false,
  personal: { ...emptyPersonal },
  identity: {},
  signaturePng: null,
  knowYou: emptyKnowYou(),
  assistedBy: emptyAssistedBy(),
  personalSaved: false,
  smsVerified: false,
  emailVerified: false,
  identityVerified: false,
  signatureCaptured: false,
  knowYouSaved: false,
  submitted: null,
});

interface OnboardingContextValue {
  draft: OnboardingDraft;
  setProduct: (product: ProductType) => void;
  setConsentsAccepted: () => void;
  updatePersonal: (patch: Partial<PersonalDetails>) => void;
  markPersonalSaved: () => void;
  setOtpVerified: (kind: 'sms' | 'email') => void;
  updateIdentity: (patch: Partial<IdentityCaptures>) => void;
  markIdentityVerified: () => void;
  setSignature: (png: string | null) => void;
  updateKnowYou: (patch: Partial<KnowYouDetails>) => void;
  markKnowYouSaved: () => void;
  updateAssistedBy: (patch: Partial<AssistedByDetails>) => void;
  setSubmitted: (result: SubmitResult) => void;
  resetDraft: () => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const [draft, setDraft] = useState<OnboardingDraft>(initialDraft);

  const setProduct = useCallback((product: ProductType) => {
    setDraft((d) => ({ ...d, product }));
  }, []);

  const setConsentsAccepted = useCallback(() => {
    setDraft((d) => ({ ...d, consentsAccepted: true }));
  }, []);

  const updatePersonal = useCallback((patch: Partial<PersonalDetails>) => {
    setDraft((d) => ({ ...d, personal: { ...d.personal, ...patch } }));
  }, []);

  const markPersonalSaved = useCallback(() => {
    setDraft((d) => ({ ...d, personalSaved: true }));
  }, []);

  const setOtpVerified = useCallback((kind: 'sms' | 'email') => {
    setDraft((d) =>
      kind === 'sms' ? { ...d, smsVerified: true } : { ...d, emailVerified: true }
    );
  }, []);

  const updateIdentity = useCallback((patch: Partial<IdentityCaptures>) => {
    setDraft((d) => ({ ...d, identity: { ...d.identity, ...patch } }));
  }, []);

  const markIdentityVerified = useCallback(() => {
    setDraft((d) => ({ ...d, identityVerified: true }));
  }, []);

  const setSignature = useCallback((png: string | null) => {
    setDraft((d) => ({ ...d, signaturePng: png, signatureCaptured: !!png }));
  }, []);

  const updateKnowYou = useCallback((patch: Partial<KnowYouDetails>) => {
    setDraft((d) => ({ ...d, knowYou: { ...d.knowYou, ...patch } }));
  }, []);

  const markKnowYouSaved = useCallback(() => {
    setDraft((d) => ({ ...d, knowYouSaved: true }));
  }, []);

  const updateAssistedBy = useCallback((patch: Partial<AssistedByDetails>) => {
    setDraft((d) => ({ ...d, assistedBy: { ...d.assistedBy, ...patch } }));
  }, []);

  const setSubmitted = useCallback((result: SubmitResult) => {
    setDraft((d) => ({ ...d, submitted: result }));
  }, []);

  const resetDraft = useCallback(() => {
    setDraft(initialDraft());
  }, []);

  const value = useMemo(
    () => ({
      draft,
      setProduct,
      setConsentsAccepted,
      updatePersonal,
      markPersonalSaved,
      setOtpVerified,
      updateIdentity,
      markIdentityVerified,
      setSignature,
      updateKnowYou,
      markKnowYouSaved,
      updateAssistedBy,
      setSubmitted,
      resetDraft,
    }),
    [
      draft,
      setProduct,
      setConsentsAccepted,
      updatePersonal,
      markPersonalSaved,
      setOtpVerified,
      updateIdentity,
      markIdentityVerified,
      setSignature,
      updateKnowYou,
      markKnowYouSaved,
      updateAssistedBy,
      setSubmitted,
      resetDraft,
    ]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextValue => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return ctx;
};

// Earliest incomplete step → its route (PHASE1_FLOWS §3 guards).
export const earliestIncompleteRoute = (draft: OnboardingDraft): string => {
  if (!draft.product) return '/open-account';
  if (!draft.personalSaved) return '/open-account/personal-details';
  if (!draft.smsVerified) return '/open-account/sms-verification';
  if (!draft.emailVerified) return '/open-account/email-verification';
  if (!draft.identityVerified) return '/open-account/identity-verification';
  if (!draft.signatureCaptured) return '/open-account/signature-capture';
  if (!draft.knowYouSaved) return '/open-account/know-you';
  return '/open-account/assisted-by';
};
